import { Suspense, lazy, useEffect } from 'react';
import { NavLink, Navigate, Outlet, Route, Routes, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { apiRequest, setCsrfToken } from './api/client';
import {
  administrationPermissions,
  isDeploymentOpen,
  listPlatformSections,
  listSettingsSections,
} from '@dashboard/contracts';
import { useAuthStatus, useScheduleInboxSummary } from './api/queries';
import { SessionProvider, useSession } from './auth/session-context';
import { FleetPage } from './routes/fleet-page';
import { ProjectGate } from './project/active-project';
import { changeLocale } from './i18n';
import { ThemeProvider } from './theme/theme-provider';
import { CapturePage, rememberProject } from './capture/capture-page';
import { ErrorState, LoadingState, PermissionDeniedState } from './components/ui/states';
import { ErrorBoundary } from './components/ui/error-boundary';
import { GearIcon, MailIcon, PersonIcon, SlidersIcon } from './components/ui/icons';
import type { Permission } from '@dashboard/contracts';
import { SignedOutPage } from './routes/signed-out-page';

/**
 * Route-level code splitting (section 15.2). The dashboard is the landing
 * route and stays in the main bundle; the rest load on navigation, so the
 * initial payload is the shell plus what the user actually asked for.
 */
const DashboardPage = lazy(() =>
  import('./routes/dashboard-page').then((module) => ({ default: module.DashboardPage })),
);
const ProjectPage = lazy(() =>
  import('./routes/project-page').then((module) => ({ default: module.ProjectPage })),
);
const InfrastructurePage = lazy(() =>
  import('./routes/infrastructure-page').then((module) => ({
    default: module.InfrastructurePage,
  })),
);
const AuditPage = lazy(() =>
  import('./routes/audit-page').then((module) => ({ default: module.AuditPage })),
);
const IntegrationsPage = lazy(() =>
  import('./routes/integrations-page').then((module) => ({ default: module.IntegrationsPage })),
);
const SchedulePage = lazy(() =>
  import('./routes/schedule-page').then((module) => ({ default: module.SchedulePage })),
);
const MapPage = lazy(() =>
  import('./routes/map-page').then((module) => ({ default: module.MapPage })),
);
const MeetingsPage = lazy(() =>
  import('./routes/meetings-page').then((module) => ({ default: module.MeetingsPage })),
);
const RegisterPage = lazy(() =>
  import('./routes/register-page').then((module) => ({ default: module.RegisterPage })),
);
const SettingsPage = lazy(() =>
  import('./routes/settings-page').then((module) => ({ default: module.SettingsPage })),
);
const InboxPage = lazy(() =>
  import('./routes/inbox-page').then((module) => ({ default: module.InboxPage })),
);
const CostsPage = lazy(() =>
  import('./routes/costs-page').then((module) => ({ default: module.CostsPage })),
);
const UsersPage = lazy(() =>
  import('./routes/users-page').then((module) => ({ default: module.UsersPage })),
);
const PeoplePage = lazy(() =>
  import('./routes/people-page').then((module) => ({ default: module.PeoplePage })),
);
const ProfilePage = lazy(() =>
  import('./routes/profile-page').then((module) => ({ default: module.ProfilePage })),
);
const DocumentsPage = lazy(() =>
  import('./routes/documents-page').then((module) => ({ default: module.DocumentsPage })),
);
const AdminPage = lazy(() =>
  import('./routes/admin-page').then((module) => ({ default: module.AdminPage })),
);
/* Not lazy. The whole point is a device with no network, and a chunk that has
   to be fetched is a chunk that is not there when it is needed -- the service
   worker caches it only after a first visit, and a first visit is exactly the
   case this must not fail. */
const CAPTURE_PATH = '/capture';

export function App() {
  const status = useAuthStatus();
  const location = useLocation();

  useEffect(() => {
    if (status.data?.authenticated) {
      setCsrfToken(status.data.context.csrfToken);
      void changeLocale(status.data.context.user.locale);
      /* Remembered for the capture screen, which runs on a device that cannot
         ask. The project list is an API read and the API is the thing that is
         missing out there, so the last one this device saw is what it uses. */
      const project = status.data.context.project;
      if (project) rememberProject({ id: project.id, name: project.name });
    } else {
      setCsrfToken(null);
    }
  }, [status.data]);

  /*
   * Capture renders before the session does, and deliberately.
   *
   * Everywhere else a failed `/auth/context` is an error screen, correctly:
   * there is nothing to show. Here it is the ordinary case -- somebody standing
   * next to a hazard with no signal -- and an error screen would lose the note
   * they came to write. Queuing needs no authority; sending does, and sending
   * only happens when there is network, which is when the session is back.
   */
  if (location.pathname === CAPTURE_PATH) {
    return (
      <ThemeProvider>
        <CapturePage />
      </ThemeProvider>
    );
  }

  if (status.isPending) {
    return (
      <div className="centered-page">
        <LoadingState rows={3} />
      </div>
    );
  }

  if (status.isError) {
    return (
      <div className="centered-page">
        <div className="centered-card">
          <ErrorState error={status.error} onRetry={() => void status.refetch()} />
        </div>
      </div>
    );
  }

  if (!status.data.authenticated) {
    return (
      <ThemeProvider>
        <SignedOutPage loginUrl={status.data.loginUrl} />
      </ThemeProvider>
    );
  }

  const context = status.data.context;

  /*
   * A deployment that is not open for business (contracts: deployment.ts).
   *
   * The API refuses every tenant request in this state, so rendering the shell
   * would produce a product whose every panel is an error. The provider is let
   * through -- working on a deployment before it opens is the entire point of
   * the provisioning stage -- and everyone else gets one honest sentence and a
   * way out.
   */
  if (!isDeploymentOpen(context.deployment.stage) && context.platformPermissions.length === 0) {
    return (
      <ThemeProvider theme={context.organization.theme}>
        <ClosedDeploymentPage
          brand={context.organization.theme.brand.name}
          stage={context.deployment.stage}
        />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={context.organization.theme}>
      <SessionProvider session={context}>
        <AppShell />
      </SessionProvider>
    </ThemeProvider>
  );
}

/**
 * What a client sees while their installation is closed.
 *
 * Deliberately not an error page: nothing has gone wrong that they can act on,
 * and a stack of failed requests behind a retry button would invite them to
 * keep pressing it. Sign-out is offered because it is the only thing left that
 * works, and a session they cannot end is worse than no session.
 */
function ClosedDeploymentPage({ brand, stage }: { brand: string; stage: string }) {
  const { t } = useTranslation();

  return (
    <div className="centered-page">
      <div className="centered-card">
        <h1>{brand}</h1>
        <p>{t(`platform.closed.${stage}`)}</p>
        <SignOutButton />
      </div>
    </div>
  );
}

/**
 * The mailbox, as an icon in the header controls rather than a word in the
 * navigation.
 *
 * It sits with the other things that are always available and belong to the
 * person rather than to the page -- language, theme, sign out -- because that
 * is where a reader looks for their own mail, and because a labelled entry in
 * the section listing the app's *screens* implied the mailbox was another
 * area of the product to work in. It is a place you dip into and leave.
 *
 * The shell knowing about one feature's notifications is a real coupling, and
 * it is paid for deliberately: a request for acceptance blocks somebody else's
 * work, and a notification only visible on the page you would have to already
 * be looking at is a notification that arrives late. The mailbox is the thing
 * that generalizes -- a second domain adds to this list rather than opening a
 * second one.
 *
 * Two states, not one. A dot means something is unread; a number means that
 * many people are blocked on you, which deserves a stronger mark than "you
 * have mail". Neither is carried by colour alone: the count is a numeral, and
 * every state names itself in the accessible label, since a coloured dot on an
 * envelope says nothing to a screen reader (section 12.4).
 */
function InboxNavLink() {
  const { t } = useTranslation();
  const summary = useScheduleInboxSummary();
  const waiting = summary.data?.pendingApprovalCount ?? 0;
  const unread = summary.data?.unreadCount ?? 0;

  const label =
    waiting > 0
      ? t('inbox.navWaiting', { count: waiting })
      : unread > 0
        ? t('inbox.navUnread', { count: unread })
        : t('nav.inbox');

  return (
    <NavLink className="icon-link" to="/inbox" aria-label={label} title={label}>
      <MailIcon />
      {waiting > 0 ? (
        <span className="icon-link__badge" aria-hidden="true">
          {waiting}
        </span>
      ) : unread > 0 ? (
        <span className="icon-link__dot" aria-hidden="true" />
      ) : null}
    </NavLink>
  );
}

/**
 * System settings, shown only to somebody who can configure something.
 *
 * The catalog answers that: no readable sections, no entry. An administrative
 * area that opens on an empty page is worse than one that is not offered, and
 * this way enabling a capability for a role reveals its settings in the same
 * motion -- there is no second list to remember to update.
 */
function SettingsNavLink() {
  const { t } = useTranslation();
  const { session } = useSession();

  if (listSettingsSections(session.permissions).length === 0) return null;

  return (
    <NavLink
      className="icon-link"
      to="/settings"
      aria-label={t('settings.title')}
      title={t('settings.title')}
    >
      <GearIcon />
    </NavLink>
  );
}

/**
 * The provider console, shown only to the service provider's own accounts.
 *
 * Its own entry rather than a section inside `SettingsNavLink`'s page, and its
 * own icon rather than a second gear, because the two configure different
 * things for different people: the gear changes how the client's product
 * behaves, this changes what the client's product is. A support engineer who
 * confuses them has just re-themed a live deployment.
 */
function AdminNavLink() {
  const { t } = useTranslation();
  const { session } = useSession();

  if (listPlatformSections(session.platformPermissions).length === 0) return null;

  return (
    <NavLink
      className="icon-link icon-link--platform"
      to="/admin"
      aria-label={t('platform.title')}
      title={t('platform.title')}
    >
      <SlidersIcon />
    </NavLink>
  );
}

/**
 * A person's own settings, distinct from the administrative panel behind
 * `SettingsNavLink`: every signed-in user gets this one, whether or not they
 * can configure anything system-wide, because it is where their own display
 * name, language and colour mode live -- not a capability that has to be
 * granted. Language and colour mode used to be direct controls in this
 * header; they moved here so the header holds only navigation and the things
 * that belong to the whole session (the mailbox, signing out), and a person
 * has one place to find everything about their own account.
 */
function ProfileNavLink() {
  const { t } = useTranslation();
  return (
    <NavLink
      className="icon-link"
      to="/profile"
      aria-label={t('profile.title')}
      title={t('profile.title')}
    >
      <PersonIcon />
    </NavLink>
  );
}

/**
 * Route element for the screens a permission governs.
 *
 * The navigation already hides what a reader cannot use, but hiding a link is
 * not a gate: a bookmark, a shared URL, a browser's history or a permission
 * revoked while a tab sat open all land on the page anyway. The API refuses
 * every request correctly -- that was never in question -- so what the reader
 * actually got was the full screen furniture with a cascade of generic errors
 * inside it, which reads as the product being broken rather than as access
 * being denied.
 *
 * Stated once in the router, next to the routes it governs, for the same
 * reason `ProjectGate` states its precondition there: the alternative is the
 * same early return copied into the top of six pages, where one of them will
 * eventually be missed.
 *
 * This is a usability affordance and nothing more. The server re-checks every
 * request (section 13.2), and a reader who removed this element from the DOM
 * would gain exactly nothing.
 */
export function PermissionGate({
  permission,
}: {
  /** One permission, or several of which any one admits. */
  permission: Permission | readonly Permission[];
}) {
  const { canAny } = useSession();
  const any = typeof permission === 'string' ? [permission] : permission;

  if (!canAny(...any)) return <PermissionDeniedState />;

  return <Outlet />;
}

/**
 * Where "/" and an unknown URL lead.
 *
 * The dashboards, for the client's own staff. For a member of another
 * organization, the clean-vehicle register: it is the part of the product
 * opened to them (docs/organizations-design.md section 3.3), and a dashboard
 * list that can only ever hold their own views is not a front page.
 */
function HomeRedirect() {
  const { isExternal, canAny } = useSession();
  return <Navigate to={isExternal && canAny('fleet.read') ? '/fleet' : '/dashboards'} replace />;
}

/**
 * The last line between a render error and a blank page.
 *
 * Keyed on the pathname so navigating away clears a caught error: a boundary
 * that has caught stays caught until it is remounted, and without the key one
 * bad render would leave the reader on a screen they cannot leave.
 *
 * Inside the shell rather than around it, deliberately -- the header and its
 * navigation keep working, so the reader has somewhere to go. A boundary
 * wrapped around everything would replace the entire application with the
 * fallback and offer no way out but a reload.
 */
function RouteErrorBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  return (
    <ErrorBoundary
      label={`route:${location.pathname}`}
      resetKey={location.pathname}
      fallback={(error, reset) => <ErrorState error={error} onRetry={reset} />}
    >
      {children}
    </ErrorBoundary>
  );
}

function AppShell() {
  const { t } = useTranslation();
  const { session, canAny, isExternal } = useSession();

  return (
    <div className="app-shell">
      {/* Keyboard users must be able to skip the navigation (section 18). */}
      <a className="skip-link" href="#main">
        {t('app.skipToContent')}
      </a>

      <header className="app-header">
        {/*
          Brand, then project. The deployment is about one project
          (architecture documentation section 2), so its name belongs in the
          furniture that never changes rather than in a control repeated on
          every page: stated once, where a reader can confirm what they are
          looking at without it being something they could accidentally change.
        */}
        {/*
          The brand mark, when one is configured.
          The provider console has collected `logoUrl` since branding was built
          and nothing ever rendered it -- a form field that quietly did nothing.
          It sits beside the name rather than replacing it: a logo alone leaves
          a reader with no accessible text for the deployment they are looking
          at, and the name is what the closed-deployment page and the document
          title already use.
        */}
        {session.organization.theme.brand.logoUrl ? (
          <img
            className="app-brand-mark"
            src={session.organization.theme.brand.logoUrl}
            alt=""
            aria-hidden="true"
          />
        ) : null}
        <span className="app-brand">{session.organization.theme.brand.name}</span>
        {session.project && session.memberOf.isHost ? (
          <span className="app-project" title={session.project.key}>
            {session.project.name}
          </span>
        ) : null}
        {/* Whose data this is, for a member of another organization: everything
            they see is scoped to it, and the header says so once instead of
            every page. The project is not theirs to read, so it gives way. */}
        {session.memberOf.isHost ? null : (
          <span className="app-project app-member-of">{session.memberOf.name}</span>
        )}

        <nav className="app-nav" aria-label={t('app.title')}>
          <NavLink to="/dashboards">{t('nav.dashboards')}</NavLink>
          {canAny('project.read') ? <NavLink to="/project">{t('nav.project')}</NavLink> : null}
          {canAny('schedule.read') ? <NavLink to="/schedule">{t('nav.schedule')}</NavLink> : null}
          {canAny('map.read') ? <NavLink to="/map">{t('nav.map')}</NavLink> : null}
          {canAny('cost.read') ? <NavLink to="/costs">{t('nav.costs')}</NavLink> : null}
          {canAny('meeting.read') ? <NavLink to="/meetings">{t('nav.meetings')}</NavLink> : null}
          {canAny('register.read') ? <NavLink to="/register">{t('nav.register')}</NavLink> : null}
          {canAny('fleet.read') ? <NavLink to="/fleet">{t('nav.fleet')}</NavLink> : null}
          {canAny('document.read') ? <NavLink to="/documents">{t('nav.documents')}</NavLink> : null}
          {canAny('infrastructure.read') ? (
            <NavLink to="/infrastructure">{t('nav.infrastructure')}</NavLink>
          ) : null}
          {canAny('integration.read') ? (
            <NavLink to="/integrations">{t('nav.integrations')}</NavLink>
          ) : null}
          {canAny('audit.read') ? <NavLink to="/audit">{t('nav.audit')}</NavLink> : null}
          {/* Everybody's: the directory is scoped by the reader's reach. */}
          <NavLink to="/people">{t('nav.people')}</NavLink>
          {/* Users, the organizations they belong to, and roles: one page, a
              tab for each right. */}
          {canAny(...administrationPermissions('read'), 'party.manage') ? (
            <NavLink to="/users">{t('nav.users')}</NavLink>
          ) : null}
        </nav>

        <div className="app-header-end">
          {canAny('schedule.read') && !isExternal ? <InboxNavLink /> : null}
          <AdminNavLink />
          <SettingsNavLink />
          <ProfileNavLink />
          <SignOutButton />
        </div>
      </header>

      <main className="app-main" id="main">
        <RouteErrorBoundary>
          <Suspense fallback={<LoadingState rows={4} />}>
            <Routes>
              <Route path="/" element={<HomeRedirect />} />
              <Route path="/dashboards" element={<DashboardPage />} />
              <Route path="/dashboards/:slug" element={<DashboardPage />} />
              {/*
              The screens that mean nothing without a project, grouped so the
              precondition is stated once in the router instead of at the top of
              each page. Settings is not among them: it also holds
              organization-wide sections, and those still work.
            */}
              <Route element={<ProjectGate />}>
                <Route element={<PermissionGate permission="project.read" />}>
                  <Route path="/project" element={<ProjectPage />} />
                </Route>
                <Route element={<PermissionGate permission="schedule.read" />}>
                  <Route path="/schedule" element={<SchedulePage />} />
                </Route>
                <Route element={<PermissionGate permission="map.read" />}>
                  <Route path="/map" element={<MapPage />} />
                </Route>
                <Route element={<PermissionGate permission="cost.read" />}>
                  <Route path="/costs" element={<CostsPage />} />
                </Route>
                <Route element={<PermissionGate permission="meeting.read" />}>
                  <Route path="/meetings" element={<MeetingsPage />} />
                </Route>
                <Route element={<PermissionGate permission="register.read" />}>
                  <Route path="/register" element={<RegisterPage />} />
                </Route>
                <Route element={<PermissionGate permission="document.read" />}>
                  <Route path="/documents" element={<DocumentsPage />} />
                </Route>
              </Route>
              {/* Outside `ProjectGate`, and the first screen in the product that
                is: a framework agreement for waste collection is not part of an
                infrastructure project, so the fleet is addressed by organization
                alone. The tab strip inside gates each tab on its own
                permission. */}
              <Route element={<PermissionGate permission="fleet.read" />}>
                <Route path="/fleet" element={<FleetPage />} />
              </Route>
              {/* The portfolio listing this replaced; saved links still land. */}
              <Route path="/projects" element={<Navigate to="/project" replace />} />
              {/* The mailbox is scheduling's, so it answers to the same
                permission the navigation entry does. Not a supplier's: the mailbox
                is where the client's changes are approved, and nothing in it is
                opened to another organization (docs/organizations-design.md 9.3). */}
              <Route element={<PermissionGate permission="schedule.read" />}>
                <Route
                  path="/inbox"
                  element={isExternal ? <Navigate to="/" replace /> : <InboxPage />}
                />
              </Route>
              {/* Both forms render the same page; the id only chooses a section,
                so a link to one survives that section being renamed away. */}
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/settings/:sectionId" element={<SettingsPage />} />
              {/* The provider console. Same two-form shape as settings, so a link
                to one section survives that section being renamed away. */}
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/admin/:sectionId" element={<AdminPage />} />
              <Route element={<PermissionGate permission="infrastructure.read" />}>
                <Route path="/infrastructure" element={<InfrastructurePage />} />
              </Route>
              <Route element={<PermissionGate permission="integration.read" />}>
                <Route path="/integrations" element={<IntegrationsPage />} />
              </Route>
              {/* Reading the audit log is itself a privileged action, and one
                nothing else implies: `audit.read` is granted deliberately. */}
              <Route element={<PermissionGate permission="audit.read" />}>
                <Route path="/audit" element={<AuditPage />} />
              </Route>
              {/* People, the organizations they belong to, and roles, a tab
                  for each right -- and of the people, the whole directory or
                  one organization's, whichever the reader's permissions reach. */}
              <Route
                element={
                  <PermissionGate
                    permission={[...administrationPermissions('read'), 'party.manage']}
                  />
                }
              >
                <Route path="/users" element={<UsersPage />} />
              </Route>
              {/* Self-service, so it takes no permission and no id in the URL --
                unlike /users/:id, which is an admin editing someone else. */}
              <Route path="/profile" element={<ProfilePage />} />
              {/* No gate: the directory is scoped by the reader's reach, and
                  somebody who may see nobody else still sees their own. */}
              <Route path="/people" element={<PeoplePage />} />
              <Route path="*" element={<HomeRedirect />} />
            </Routes>
          </Suspense>
        </RouteErrorBoundary>
      </main>
    </div>
  );
}

function SignOutButton() {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      className="button"
      onClick={() => {
        void apiRequest('/api/v1/auth/logout', { method: 'POST' }).finally(() => {
          // A full reload rather than a state update: it discards every cached
          // query, so no signed-in data survives in memory after sign-out.
          window.location.assign('/');
        });
      }}
    >
      {t('app.signOut')}
    </button>
  );
}
